// App.jsx（全文：テーブル駆動で Provider ネストを生成 / Year は“必要な場所だけ”）
//
// ✅ 目的
// - useMemo地獄（Contextごとの value オブジェクト大量定義）を撤去
// - Provider構造を “テーブル1つ” で管理して読みやすくする
// - Year1/Year2 は修正前互換のため「Map/Map3/CesiumContainer だけ」に限定
// - 他コンポーネントは触らない

import React, { createContext, useEffect, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import { photo } from "./cesiumsetting";
import { Citygml2kml } from "./citygml/citygml2kml";
import { CesiumContainer } from "./coreCesium/cesiumcontainer";
// import { CesiumContainer3 } from "./coreCesium/cesiumcontainer3";

import { Faq } from "./geograph/faq";
import { Howto } from "./geograph/howto";
import { About } from "./geograph/pages/about";
import { Tags } from "./geograph/tgsl";
import { Home } from "./home";
import { Map } from "./map";
import { Map3 } from "./mieruka/map3";
import { Select } from "./select";

import { Devtools } from "./devtools/index";
import { Teachersupport } from "./geograph/techersupport";
import RedirectToHome from "./geograph/pages/RedirectToHome";

// import data_meta1 from "./geograph/metadatas/meta_data_20260205";
// import meta_mieruka from "./mieruka/meta_mieruka_20260121";
// const data_meta = data_meta1.concat(meta_mieruka);

import { data_meta, data_meta_map } from "./importmetadata";
import { Teikokusupport } from "./geograph/teikokusupport";
import MapLibreTriple2D from "./geo2d/geo2d/MapLibreTriple2D";

// =====================
// Contexts（現状維持）
// =====================
export const ModeSet = createContext(1);
export const StartmodalSet = createContext(true);
export const KakudaimodalSet = createContext(true);

export const MetadataSet = createContext([]);

export const MetadataContext = createContext({
  meta: [],
  setMeta: () => {
    //
  },
});

export const FullscreenSet = createContext(false);
export const FullviewerSet = createContext(0);
export const RendouSet = createContext(true);
export const SyukurenSet = createContext(false);
export const SyosaiSet = createContext(false);
export const ModalcolorSet = createContext(false);

export const Rotate0Set = createContext(false);
export const Rotate1Set = createContext(false);
export const Rotate2Set = createContext(false);

export const Map1Set = createContext(photo);
export const Map2Set = createContext(photo);
export const Theme1Set = createContext("");
export const Theme2Set = createContext("");

export const Zoomlevel1Set = createContext(1);
export const Zoomlevel2Set = createContext(1);
export const Zoomhanrei1Set = createContext(null);
export const Zoomhanrei2Set = createContext(null);

export const Static2Map1Set = createContext([]);
export const Static2Map2Set = createContext([]);
export const Area1Set = createContext("");
export const Area2Set = createContext("");

// Year Context は残すが、全ページ共通Providerでは付けない（必要な場所だけ付ける）
export const Year1Set = createContext(0);
export const Year2Set = createContext(0);

export const Alpha1Set = createContext(1);
export const Alpha2Set = createContext(1);

export const Color1Set = createContext([]);
export const Color2Set = createContext([]);
export const Shikii1Set = createContext([]);
export const Shikii2Set = createContext([]);

export const Cesium1Set = createContext("");
export const Cesium2Set = createContext("");

export const PromodeSet = createContext(null);

export const Ratio1Set = createContext(1);
export const Ratio2Set = createContext(1);
export const ShowratioSet = createContext(0);

// =====================
// localStorage Hook（互換救済あり）
// =====================
function useLocalStorageState(key, initialValue) {
  const [value, setValue] = useState(() => {
    if (typeof window === "undefined") return initialValue;

    const raw = window.localStorage.getItem(key);
    if (raw == null) return initialValue;

    try {
      return JSON.parse(raw);
    } catch {
      // 旧実装は setItem(key, value) で文字列化事故が起きうるので、
      // 初期値が配列/オブジェクトなら「復元不能」とみなして初期値へ戻す。
      const shouldBeArray = Array.isArray(initialValue);
      const shouldBeObject =
        initialValue &&
        typeof initialValue === "object" &&
        !Array.isArray(initialValue);

      if (shouldBeArray || shouldBeObject) return initialValue;
      return raw;
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // 容量/権限などは握りつぶし
    }
  }, [key, value]);

  return [value, setValue];
}

// =====================
// state生成（ここが1回だけ呼ばれる）
// =====================
function useAppState() {
  // --- localStorage系 ---
  // ✅ 旧挙動互換：modesetは常に string 化（子側が "8" 比較でも壊れない）
  const [modesetRaw, setModeSetRaw] = useLocalStorageState("modeset", "1");
  const modeset = String(modesetRaw);
  const setModeSet = (v) => setModeSetRaw(String(v));

  const [startmodalset, setStartmodalSet] = useLocalStorageState(
    "modal_start",
    true,
  );
  const [kakudaimodalset, setKakudaimodalSet] = useLocalStorageState(
    "modal_kakudai",
    true,
  );

  const [static2map1set, setStatic2Map1Set] = useLocalStorageState(
    "static1map",
    [],
  );
  const [static2map2set, setStatic2Map2Set] = useLocalStorageState(
    "static2map",
    [],
  );

  const [area1set, setArea1Set] = useLocalStorageState("area1set", "");
  const [area2set, setArea2Set] = useLocalStorageState("area2set", "");

  // --- 通常state ---
  const [metadataset, setMetadataSet] = useState([]);
  const [meta, setMeta] = useState([]); // 配列で統一

  const [fullscreenset, setFullscreenSet] = useState(false);
  const [fullviewerset, setFullviewerSet] = useState(0);
  const [rendouset, setRendouSet] = useState(true);
  const [syukurenset, setSyukurenSet] = useState(false);
  const [syosaiset, setSyosaiSet] = useState(false);
  const [modalcolorset, setModalcolorSet] = useState(false);

  const [rotate0set, setRotate0Set] = useState(false);
  const [rotate1set, setRotate1Set] = useState(false);
  const [rotate2set, setRotate2Set] = useState(false);

  const [map1set, setMap1Set] = useState(photo);
  const [map2set, setMap2Set] = useState(photo);
  const [theme1set, setTheme1Set] = useState("");
  const [theme2set, setTheme2Set] = useState("");

  const [zoomlevel1set, setZoomlevel1Set] = useState(1);
  const [zoomlevel2set, setZoomlevel2Set] = useState(1);
  const [zoomhanrei1set, setZoomhanrei1Set] = useState(null);
  const [zoomhanrei2set, setZoomhanrei2Set] = useState(null);

  const [showratioset, setShowratioSet] = useState(false);
  const [ratio1set, setRatio1Set] = useState(1);
  const [ratio2set, setRatio2Set] = useState(1);

  // Yearは state としては維持（ただし Provider は必要な場所だけで付ける）
  const [year1set, setYear1Set] = useState(0);
  const [year2set, setYear2Set] = useState(0);

  const [alpha1set, setAlpha1Set] = useState(1);
  const [alpha2set, setAlpha2Set] = useState(1);

  const [color1set, setColor1Set] = useState([]);
  const [color2set, setColor2Set] = useState([]);
  const [shikii1set, setShikii1Set] = useState([]);
  const [shikii2set, setShikii2Set] = useState([]);

  const [cesium1set, setCesium1Set] = useState("");
  const [cesium2set, setCesium2Set] = useState("");

  const [promodeset, setPromodeSet] = useState(null);

  // ✅ 旧挙動寄せ：空なら常に補正（修正前の“強い保険”に近づける）
  useEffect(() => {
    if (!area1set) setArea1Set("福岡県糸島市");
    if (!area2set) setArea2Set("福岡県糸島市");
  }, [area1set, area2set, setArea1Set, setArea2Set]);

  useEffect(() => {
    if (!metadataset || metadataset.length === 0) {
      setMetadataSet(data_meta);
    }
  }, [metadataset]);

  return {
    // localStorage系
    modeset,
    setModeSet,
    startmodalset,
    setStartmodalSet,
    kakudaimodalset,
    setKakudaimodalSet,
    static2map1set,
    setStatic2Map1Set,
    static2map2set,
    setStatic2Map2Set,
    area1set,
    setArea1Set,
    area2set,
    setArea2Set,

    // 通常state
    metadataset,
    setMetadataSet,
    meta,
    setMeta,
    fullscreenset,
    setFullscreenSet,
    fullviewerset,
    setFullviewerSet,
    rendouset,
    setRendouSet,
    syukurenset,
    setSyukurenSet,
    syosaiset,
    setSyosaiSet,
    modalcolorset,
    setModalcolorSet,
    rotate0set,
    setRotate0Set,
    rotate1set,
    setRotate1Set,
    rotate2set,
    setRotate2Set,
    map1set,
    setMap1Set,
    map2set,
    setMap2Set,
    theme1set,
    setTheme1Set,
    theme2set,
    setTheme2Set,
    zoomlevel1set,
    setZoomlevel1Set,
    zoomlevel2set,
    setZoomlevel2Set,
    zoomhanrei1set,
    setZoomhanrei1Set,
    zoomhanrei2set,
    setZoomhanrei2Set,
    showratioset,
    setShowratioSet,
    ratio1set,
    setRatio1Set,
    ratio2set,
    setRatio2Set,

    // Year
    year1set,
    setYear1Set,
    year2set,
    setYear2Set,

    alpha1set,
    setAlpha1Set,
    alpha2set,
    setAlpha2Set,
    color1set,
    setColor1Set,
    color2set,
    setColor2Set,
    shikii1set,
    setShikii1Set,
    shikii2set,
    setShikii2Set,
    cesium1set,
    setCesium1Set,
    cesium2set,
    setCesium2Set,
    promodeset,
    setPromodeSet,
  };
}

// =====================
// Year Provider（必要な場所だけ付ける）
// =====================
function YearProviders({ s, children }) {
  return (
    <Year1Set.Provider
      value={{ year1set: s.year1set, setYear1Set: s.setYear1Set }}
    >
      <Year2Set.Provider
        value={{ year2set: s.year2set, setYear2Set: s.setYear2Set }}
      >
        {children}
      </Year2Set.Provider>
    </Year1Set.Provider>
  );
}

// =====================
// テーブル駆動 Provider（Yearは含めない）
// =====================
const PROVIDER_TABLE = [
  // 画面/メタ
  { Ctx: FullscreenSet, v: "fullscreenset", s: "setFullscreenSet" },
  { Ctx: MetadataSet, v: "metadataset", s: "setMetadataSet" },
  { Ctx: MetadataContext, v: "meta", s: "setMeta" },

  // ビュー/連動など
  { Ctx: FullviewerSet, v: "fullviewerset", s: "setFullviewerSet" },
  { Ctx: RendouSet, v: "rendouset", s: "setRendouSet" },
  { Ctx: SyukurenSet, v: "syukurenset", s: "setSyukurenSet" },
  { Ctx: SyosaiSet, v: "syosaiset", s: "setSyosaiSet" },
  { Ctx: ModalcolorSet, v: "modalcolorset", s: "setModalcolorSet" },

  // 回転
  { Ctx: Rotate0Set, v: "rotate0set", s: "setRotate0Set" },
  { Ctx: Rotate1Set, v: "rotate1set", s: "setRotate1Set" },
  { Ctx: Rotate2Set, v: "rotate2set", s: "setRotate2Set" },

  // マップ/テーマ
  { Ctx: Map1Set, v: "map1set", s: "setMap1Set" },
  { Ctx: Map2Set, v: "map2set", s: "setMap2Set" },
  { Ctx: Theme1Set, v: "theme1set", s: "setTheme1Set" },
  { Ctx: Theme2Set, v: "theme2set", s: "setTheme2Set" },

  // ズーム/凡例
  { Ctx: Zoomlevel1Set, v: "zoomlevel1set", s: "setZoomlevel1Set" },
  { Ctx: Zoomlevel2Set, v: "zoomlevel2set", s: "setZoomlevel2Set" },
  { Ctx: Zoomhanrei1Set, v: "zoomhanrei1set", s: "setZoomhanrei1Set" },
  { Ctx: Zoomhanrei2Set, v: "zoomhanrei2set", s: "setZoomhanrei2Set" },

  // static map / area
  { Ctx: Static2Map1Set, v: "static2map1set", s: "setStatic2Map1Set" },
  { Ctx: Static2Map2Set, v: "static2map2set", s: "setStatic2Map2Set" },
  { Ctx: Area1Set, v: "area1set", s: "setArea1Set" },
  { Ctx: Area2Set, v: "area2set", s: "setArea2Set" },

  // alpha / color / shikii
  { Ctx: Alpha1Set, v: "alpha1set", s: "setAlpha1Set" },
  { Ctx: Alpha2Set, v: "alpha2set", s: "setAlpha2Set" },
  { Ctx: Color1Set, v: "color1set", s: "setColor1Set" },
  { Ctx: Color2Set, v: "color2set", s: "setColor2Set" },
  { Ctx: Shikii1Set, v: "shikii1set", s: "setShikii1Set" },
  { Ctx: Shikii2Set, v: "shikii2set", s: "setShikii2Set" },

  // モード/モーダル
  { Ctx: ModeSet, v: "modeset", s: "setModeSet" },
  { Ctx: StartmodalSet, v: "startmodalset", s: "setStartmodalSet" },
  { Ctx: KakudaimodalSet, v: "kakudaimodalset", s: "setKakudaimodalSet" },

  // 比率
  { Ctx: Ratio1Set, v: "ratio1set", s: "setRatio1Set" },
  { Ctx: Ratio2Set, v: "ratio2set", s: "setRatio2Set" },
  { Ctx: ShowratioSet, v: "showratioset", s: "setShowratioSet" },

  // Cesium
  { Ctx: Cesium1Set, v: "cesium1set", s: "setCesium1Set" },
  { Ctx: Cesium2Set, v: "cesium2set", s: "setCesium2Set" },

  // Pro
  { Ctx: PromodeSet, v: "promodeset", s: "setPromodeSet" },
];

function AppProvidersWithState({ s, children }) {
  const makeValue = (row) => ({ [row.v]: s[row.v], [row.s]: s[row.s] });

  return PROVIDER_TABLE.reduceRight((acc, row) => {
    const value = makeValue(row);
    return <row.Ctx.Provider value={value}>{acc}</row.Ctx.Provider>;
  }, children);
}

// =====================
// App（Routesツリーと Cesiumツリー を分離）
// =====================
const App = () => {
  const s = useAppState(); // ✅ state生成はここで1回だけ

  return (
    <div className="text-center z-10">
      <BrowserRouter>
        {/* ツリー1：ルーティング（UI側） */}
        <AppProvidersWithState s={s}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/citygml2kml" element={<Citygml2kml />} />
            <Route path="/about" element={<About />} />
            <Route path="/howto" element={<Howto />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/devtools/:id" element={<Devtools />} />
            <Route path="/teachersupport/:id" element={<Teachersupport />} />
            <Route path="/teikokusupport/:id" element={<Teikokusupport />} />
            <Route path="/tgsl/:tag" element={<Tags />} />
            <Route path="/select" element={<Select />} />
            <Route path="/map2d/:mode/:no/:area" element={
              <div className="absolute z-30">
                <MapLibreTriple2D />
              </div>
              }
            />
            {/* ✅ Yearが必要な世界だけ YearProviders で包む（修正前互換） */}
            <Route
              path="/map/:mode/:no/:area"
              element={
                <YearProviders s={s}>
                  <Map />
                </YearProviders>
              }
            />
            <Route
              path="/map3/:mode/:no/:area"
              element={
                <YearProviders s={s}>
                  <Map3 />
                </YearProviders>
              }
            />

            <Route path="*" element={<RedirectToHome />} />
          </Routes>
        </AppProvidersWithState>

        {/* ツリー2：CesiumContainer（常駐側） */}
        <AppProvidersWithState s={s}>
          <YearProviders s={s}>
            <CesiumContainer />
            {/* 必要なら Map3用も同様に */}
            {/* <CesiumContainer3 /> */}
          </YearProviders>
        </AppProvidersWithState>
      </BrowserRouter>
    </div>
  );
};

export default App;
